import { DownOutlined, FileDoneOutlined, InfoCircleOutlined } from "@ant-design/icons";
import {
  Breadcrumb,
  Button,
  DatePicker,
  Divider,
  Dropdown,
  Empty,
  Form,
  Input,
  Layout,
  Modal,
  Result,
  Select,
  Skeleton,
  Space,
  Table,
  Tooltip,
  notification,
  theme
} from "antd";
import dayjs from "dayjs";
import React, {useEffect, useRef, useState} from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiTransactionDownload, apiUnifiedTransactionsList } from "../../../api/transactions";
import { useIsMobileHook } from "../../../utils/WindowHooks";
import { formatDate, formatDateTime, formatPrice } from "../../../utils/format";
import { acquirerBadge, ACQUIRER_OPTIONS, cardBrandLabel, paymentTypeLabel, transactionStatusMeta, TRANSACTION_STATUS_OPTIONS } from "../../../utils/transactions";
import AccountsSearchSelect from "../../common/AccountsSearchSelect/AccountsSearchSelect";
import { isSuperUser } from "../../../helpers/user";
import "./SalesListing.scss";

const { RangePicker } = DatePicker;
const { Content } = Layout;
const { Option } = Select;

const SalesListing = () => {
  const [dataSales, setDataSale] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const isMobile = useIsMobileHook(1600);
  const [sellerFilter, setSellerFilter] = useState();
  const [filteredData, setFilteredData] = useState(dataSales?.results);
  const [statusFilter, setStatusFilter] = useState();
  const [paymentTypeFilter, setPaymentTypeFilter] = useState();
  const [acquirerFilter, setAcquirerFilter] = useState();
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(7, 'day'),
    dayjs().add(1, 'day')
  ]);
  const [pagination, setPagination] = useState({
    current: 1,
    total: 0,
    pageSize: 10,
  });
  const navigate = useNavigate();
  const [activeError, setActiveError] = useState(false);
  const [errorStatus, setErrorStatus] = useState("");    
  const [searchAvanced, setSearchAvanced] = useState(false);

  const handleRowClick = (record) => {
    const id = record.id;
    const newExpandedRowKeys = [...expandedRowKeys];
    const index = newExpandedRowKeys.indexOf(id);
    if (index > -1) {
      newExpandedRowKeys.splice(index, 1);
    } else {
      newExpandedRowKeys.push(id);
    }
    setExpandedRowKeys(newExpandedRowKeys);  
  };

  useEffect(() => {
    setPagination((prevPagination) => ({
      ...prevPagination,
      current: 1,
      total: 0,
    }))
    fetchData({
      page: 1,
      page_size: pagination.pageSize,
      seller: sellerFilter ? sellerFilter.id : undefined,
      status: statusFilter ? statusFilter : undefined,
      payment_type: paymentTypeFilter || undefined,
      acquirer: acquirerFilter || undefined,
      created_at_gte: dateRange[0]?.format("YYYY-MM-DD") || undefined,
      created_at_lte: dateRange[1]?.format("YYYY-MM-DD") || undefined,
    });
  }, [sellerFilter, statusFilter, paymentTypeFilter, acquirerFilter, dateRange]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async (params) => {
    setIsFetching(true);
    try {
      const filteredParams = Object.fromEntries(
        Object.entries(params).filter(([_, value]) => value !== undefined && value !== null && value !== '')
      );
      const json = await apiUnifiedTransactionsList(filteredParams);
      setDataSale(json);
      setFilteredData(json.results);
      setPagination((prevPagination) => ({
        ...prevPagination,
        total: json.count,
        current: params.page || prevPagination.current,
        pageSize: params.page_size || prevPagination.pageSize,
      }));
    } catch (error) {
      setActiveError(true);
      setErrorStatus(error.status);
      notification.error({
        message: "Erro",
        description: "Ocorreu um erro ao buscar as vendas.",
        placement: "topRight",
      });
    } finally {
      setIsFetching(false);
    }
  };

  const handleFilterChange = () => {
    const filters = {};

    if (sellerFilter) filters.seller = sellerFilter ? sellerFilter.id : undefined;
    if (statusFilter) filters.status = statusFilter;
    if (paymentTypeFilter) filters.payment_type = paymentTypeFilter;
    if (acquirerFilter) filters.acquirer = acquirerFilter;
    if (dateRange && dateRange.length === 2) {
      filters.created_at_gte = dateRange[0]?.format("YYYY-MM-DD");
      filters.created_at_lte = dateRange[1]?.format("YYYY-MM-DD");
    }

    fetchData({
      page: pagination.current,
      page_size: pagination.pageSize,
      ...filters,
    });
  };

  const onChange = (pagination) => {
    fetchData({
      page: pagination.current,
      page_size: pagination.pageSize,
      seller: sellerFilter ? sellerFilter.id : undefined,
      status: statusFilter ? statusFilter : undefined,
      payment_type: paymentTypeFilter || undefined,
      acquirer: acquirerFilter || undefined,
      created_at_gte: dateRange[0]?.format("YYYY-MM-DD") || undefined,
      created_at_lte: dateRange[1]?.format("YYYY-MM-DD") || undefined,    });
  };


  const renderStatus = (status) => {
    const meta = transactionStatusMeta(status);
    return <span className={`badge ${meta.className}`}>{meta.label}</span>;
  };

  const acquirerColumn = {
    title: "",
    key: "acquirer",
    width: 1,
    render: (_, row) => {
      const badge = acquirerBadge(row.acquirer);
      return badge ? (
        <Tooltip title={badge.label}>
          <span className={`badge badge-acquirer ${badge.className}`}>{badge.letter}</span>
        </Tooltip>
      ) : null;
    },
  };

  const columns = [
    {
      title: "Data",
      dataIndex: "created_at",
      key: "created_at",
      onCell: () => ({
        style: { cursor: "pointer" },
      }),
      render: (confirmed) => (
        <p
          style={{ textTransform: "capitalize" }}
          dangerouslySetInnerHTML={{ __html: formatDateTime(confirmed) }}
        />
      ),
      responsive: ["sm"],
    },
    {
      title: "CE",
      dataIndex: "statement_descriptor",
      key: "statement_descriptor",
      sorter: (a, b) => {
        const statusOrder = {
          1: 1,
          0: 2,
        };
        return statusOrder[a.confirmed] - statusOrder[b.confirmed];
      },
    },
    {
      title: "Tipo",
      dataIndex: "payment_type",
      key: "payment_type",
      render: (paymentType) => paymentTypeLabel(paymentType),
      responsive: ["sm"],
    },
    {
      title: "Bandeira",
      dataIndex: "card_brand",
      key: "card_brand",
      render: (cardBrand) => <p>{cardBrandLabel(cardBrand) || "—"}</p>,
      responsive: ["xxl"],
    },
    {
      title: <Tooltip title="Número Serial do P.O.S">S/N</Tooltip>,
      dataIndex: "pos_identification",
      key: "pos_identification",
      render: (value) => value || "—",
      responsive: ["sm"],
    },
    {
      title: "Valor Bruto",
      dataIndex: "amount",
      sorter: (a, b) => parseFloat(a.amount) - parseFloat(b.amount),
      key: "amount",
      render: (confirmed) => (
        <p
          style={{ textTransform: "capitalize" }}
          dangerouslySetInnerHTML={{ __html: formatPrice(confirmed) }}
        />
      ),
      responsive: ["lg"],
    },
    {
      title: "MDR",
      dataIndex: "mdr",
      key: "mdr",
      render: (value) => (
        <p dangerouslySetInnerHTML={{ __html: formatPrice(value) }} />
      ),
      responsive: ["lg"],
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: renderStatus,
      sorter: (a, b) => {
        const statusOrder = {
          succeeded: 1,
          failed: 2,
        };
        return statusOrder[a.status] - statusOrder[b.status];
      },
      responsive: ["lg"],
    },
    {
      title: "",
      dataIndex: "",
      render: (confirme) => (
        <Link to={`/transacao/${confirme.id}`} target="_blank" rel="noopener noreferrer">
          <InfoCircleOutlined style={{ cursor: "pointer", height: "100%" }} />
        </Link>
      ),
      key: "",
      responsive: ["lg"],
    },
  ];

  const expandedRowRender = (record) => (
    <div className="expandid">
      <span>
        Data:{" "}
        <p
          dangerouslySetInnerHTML={{ __html: formatDate(record.created_at) }}
        />
      </span>
      <span>
        Tipo: <p>{paymentTypeLabel(record.payment_type)}</p>
      </span>
      <span>
        Bandeira: <p>{cardBrandLabel(record.card_brand) || "—"}</p>
      </span>
      <span>
        Valor bruto:{" "}
        <p dangerouslySetInnerHTML={{ __html: formatPrice(record.amount) }} />
      </span>
      <span>
        Status:{" "}
        {renderStatus(record.status)}
      </span>
      <span style={{ marginTop: "10px" }}>
        Informação:{" "}
        <Link to={`/transacao/${record.id}`}>
          <InfoCircleOutlined style={{ cursor: "pointer", height: "100%" }} />
        </Link>
      </span>
    </div>
  );
  const locale = {
    emptyText: <Empty description="Sem dados" />,
    items_per_page: '/ pag.',
    jump_to: 'Ir para',
    jump_to_confirm: 'confirmar',
    page: 'pag.',
  };

  const renderError = (status) => {
    if (activeError) {
      return (
        <Result
          status={status === "500" ? "500" : "404"}
          title={status}
          subTitle={
            status === "500"
              ? "Ocorreu um erro no servidor, tente novamente mais tarde."
              : "Ocorreu um erro, tente novamente mais tarde."
          }
          extra={
            <Button onClick={() => navigate("/")} type="primary">
              Dashboard
            </Button>
          }
        />
      );
    }
  };

  const handleDownload = async (format) => {
    setDownloading(true);
    const filters = {
      seller: sellerFilter ? sellerFilter.id : undefined || undefined,
      status: statusFilter || undefined,
      payment_type: paymentTypeFilter || undefined,
      acquirer: acquirerFilter || undefined,
      created_at_gte: dateRange[0]?.format("YYYY-MM-DD") || undefined,
      created_at_lte: dateRange[1]?.format("YYYY-MM-DD") || undefined,
    };

    const filteredParams = Object.fromEntries(
      Object.entries(filters).filter(([_, value]) => value !== undefined)
    );

    try {
      const blob = await apiTransactionDownload(format, filteredParams);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `transacoes.${format === 'csv' ? 'csv' : 'xlsx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Failed to download file:', error);
    } finally {
      setDownloading(false);
    }
  };

  const items = [
    {
      label: 'CSV',
      key: 'csv',
      icon: <FileDoneOutlined />,
    },
    {
      label: 'EXCEL',
      key: 'excel',
      icon: <FileDoneOutlined />,
    },
  ];

  const handleMenuClick = (e) => {
    handleDownload(e.key);
  };

  const menuProps = {
    items,
    onClick: handleMenuClick,
  };

  return (
    <div className="SalesListing">
      <Layout
        style={{
          padding: "1.25rem",
        }}
      >
        <Breadcrumb
          style={{
            margin: "16px 0",
          }}
        >
          <div style={{ display: "flex", width: "100%", justifyContent: "space-between", alignItems: "center" }}>
            <h2 className="title-page">Listagem de vendas</h2>
          </div>
          <Divider />
          <Breadcrumb.Item>Minhas vendas</Breadcrumb.Item>
          <Breadcrumb.Item>Listagem de vendas</Breadcrumb.Item>
        </Breadcrumb>
        <Content
          style={{
            padding: 24,
            margin: 0,
            minHeight: 280,
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
          }}
        >
          <h3 className="subtitle-page">Listagem de vendas</h3>
          <Divider />
          {searchAvanced && <Form
            layout="inline"
            display='flex'
            style={{ marginBottom: 16, gap: '16px' }}
            onFinish={handleFilterChange}
          >
            <Form.Item label="Status">
              <Select
                placeholder="Selecione o Status"
                onChange={(values) => setStatusFilter(values)}
                style={{ width: 200 }}
                allowClear
              >
                {TRANSACTION_STATUS_OPTIONS.map((status) => (
                  <Option key={status.value} value={status.value}>
                    {status.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item label="Tipo de pagamento">
              <Select
                placeholder="Selecione o Tipo de pagamento"
                allowClear
                onChange={(value) => setPaymentTypeFilter(value)}
                style={{ width: 200 }}
              >
                <Option value="credit">Crédito</Option>
                <Option value="debit">Débito</Option>
                <Option value="pix">Pix</Option>
                <Option value="boleto">Boleto</Option>
              </Select>
            </Form.Item>
            {isSuperUser() && (
              <Form.Item label="Adquirente">
                <Select
                  placeholder="Selecione o Adquirente"
                  allowClear
                  onChange={(value) => setAcquirerFilter(value)}
                  style={{ width: 200 }}
                >
                  {ACQUIRER_OPTIONS.map((acquirer) => (
                    <Option key={acquirer.value} value={acquirer.value}>
                      {acquirer.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}
            <Form.Item>
              <Button type="primary" htmlType="submit">
                Aplicar Filtros
              </Button>
            </Form.Item>          </Form>}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <AccountsSearchSelect
                style={{ width: '200px'}}
                placeholder="Filtrar por conta"
                value={sellerFilter}
                onChange={(value) => setSellerFilter(value)}
                allowClear={true}
              />
              {!searchAvanced && <Button onClick={() => setSearchAvanced(true)}>Filtros avançados</Button>}
            </div>            <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
              <Form.Item label="Período das Transações" style={{ margin: 0 }}>
                <RangePicker
                  onChange={(dates) => setDateRange(dates)}
                  value={dateRange}
                  style={{ width: 300 }}
                  placeholder={['Data inicial', 'Data final']}
                  format={{ format: 'DD/MM/YYYY', type: 'mask' }}
                />
              </Form.Item>
            </div>
          </div>
          {isFetching ? (
            <Skeleton active />
          ) : activeError ? (
            renderError(errorStatus)
          ) : (
            <Table
              columns={isSuperUser() ? [acquirerColumn, ...columns] : columns}
              locale={locale}
              expandable={{
                expandedRowRender: isMobile ? expandedRowRender : undefined,
                expandedRowKeys: expandedRowKeys,
                onExpand: (expanded, record) => handleRowClick(record),
                responsive: ["xl"],
              }}
              dataSource={
                filteredData &&
                filteredData.map((item) => ({ ...item, key: item.id }))
              }
              pagination={{
                current: pagination.current,
                total: pagination.total,
                pageSize: pagination.pageSize,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
              }}
              bordered
              onChange={onChange}
            />
          )}

          <div className="download-section">
            <Dropdown menu={menuProps} >
              <Button loading={downloading}>
                <Space>
                  Baixar Relatório
                  <DownOutlined />
                </Space>
              </Button>
            </Dropdown>
          </div>
        </Content>
      </Layout>
    </div >
  );
};

export default SalesListing;